import React, { useState, useEffect, useRef } from 'react';
import { Badge } from './ui/badge';
import { Building2, MapPin, User, Check } from 'lucide-react';

const ClientSuggestion = ({ inputValue, clients, onSelect, className = '' }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!inputValue || inputValue.length < 2 || !clients?.length) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    const term = inputValue.toLowerCase().trim();
    const matches = clients.filter(c =>
      c.name?.toLowerCase().includes(term) ||
      c.company?.toLowerCase().includes(term)
    ).slice(0, 5);

    setSuggestions(matches);
    setIsOpen(matches.length > 0);
  }, [inputValue, clients]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isOpen || suggestions.length === 0) return null;

  const exactMatch = suggestions.find(c => c.name?.toLowerCase() === inputValue?.toLowerCase().trim());

  return (
    <div ref={ref} className={`absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg overflow-hidden ${className}`} data-testid="client-suggestions">
      {exactMatch && (
        <div className="px-3 py-2 bg-amber-500/10 border-b border-amber-500/20 flex items-center gap-2 text-xs">
          <Check className="h-3 w-3 text-amber-500" />
          <span className="text-amber-500 font-medium">Este cliente ya existe</span>
        </div>
      )}
      <div className="max-h-48 overflow-y-auto">
        {suggestions.map(client => (
          <button
            key={client.id}
            type="button"
            className="w-full px-3 py-2 text-left hover:bg-accent/50 transition-colors flex items-center gap-3 border-b border-border/50 last:border-0"
            onClick={() => { onSelect(client); setIsOpen(false); }}
            data-testid={`suggestion-${client.id}`}
          >
            <div className="p-1.5 rounded bg-primary/10 shrink-0">
              <Building2 className="h-3.5 w-3.5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{client.name}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {client.company && <span>{client.company}</span>}
                {client.contact_name && (
                  <span className="flex items-center gap-0.5">
                    <User className="h-2.5 w-2.5" />{client.contact_name}
                  </span>
                )}
                {(client.city || client.state) && (
                  <span className="flex items-center gap-0.5">
                    <MapPin className="h-2.5 w-2.5" />{[client.city, client.state].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
            </div>
            <Badge variant="outline" className="text-[10px] shrink-0">Usar</Badge>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ClientSuggestion;
